import { rootApi } from '../rootApi';

const buildQueryString = (params) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        if (Array.isArray(value)) {
            if (value.length === 0) return;
            value.forEach((v) => searchParams.append(key, v));
        } else {
            searchParams.append(key, value);
        }
    });
    return searchParams.toString();
};

const getStatusCounts = (payload) => {
    const rawCounts = payload?.statusCounts ?? payload?.StatusCounts ?? payload?.counts ?? payload?.Counts ?? [];

    if (!Array.isArray(rawCounts)) return rawCounts;

    return Object.fromEntries(
        rawCounts.map((item) => [item?.status ?? item?.Status ?? item?.statusId ?? item?.StatusId, Number(item?.count ?? item?.Count) || 0])
    );
};

export const preApprovalApi = rootApi.injectEndpoints({
    endpoints: (builder) => ({
        getAllPreApprovals: builder.query({
            query: (params) => {
                const queryString = buildQueryString(params);
                return {
                    url: `v1/preapproval?${queryString}`,
                    method: 'GET'
                };
            },
            transformResponse: (response) => {
                const payload = response?.data ?? response;
                const items = Array.isArray(payload) ? payload : payload?.items ?? payload?.Items ?? payload?.data ?? payload?.Data ?? [];
                return {
                    items: Array.isArray(items) ? items : [],
                    statusCounts: getStatusCounts(payload)
                };
            },
            onQueryStarted: async (arg, { queryFulfilled }) => {
                try {
                    await queryFulfilled;
                } catch (error) {
                    console.error('❌ getAllPreApprovals query failed:', error);
                }
            },
            providesTags: ['PreApproval']
        }),

        getPreApprovalById: builder.query({
            query: (id) => `v1/preapproval/${id}`,
            providesTags: (result, error, id) => [{ type: 'PreApproval', id }]
        }),

        createPreApproval: builder.mutation({
            query: (data) => ({
                url: 'v1/preapproval',
                method: 'POST',
                body: data
            }),
            invalidatesTags: ['PreApproval']
        }),

        updatePreApproval: builder.mutation({
            query: (data) => ({
                url: 'v1/preapproval',
                method: 'PUT',
                body: data
            }),
            invalidatesTags: (result, error, { id }) => ['PreApproval', { type: 'PreApproval', id }]
        }),

        deletePreApproval: builder.mutation({
            query: (id) => ({
                url: `v1/preapproval/${id}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['PreApproval']
        }),

        changePreApprovalStatus: builder.mutation({
            query: (data) => ({
                url: 'v1/preapproval/ChangeStatus',
                method: 'PUT',
                body: data
            }),
            invalidatesTags: ['PreApproval']
        }),

        getPreApprovalHistory: builder.query({
            query: (id) => ({
                url: `v1/preapproval/${id}/history`,
                method: 'GET'
            }),
            transformResponse: (response) => {
                if (response && typeof response === 'object') {
                    if (response.data && Array.isArray(response.data)) {
                        return response.data;
                    }
                    if (Array.isArray(response)) {
                        return response;
                    }
                }
                console.warn('⚠️ History response structure not as expected, returning empty array');
                return [];
            },
            providesTags: (result, error, id) => [{ type: 'PreApproval', id: `history-${id}` }]
        }),

        uploadPreApprovalDocument: builder.mutation({
            query: (data) => {
                const formData = new FormData();
                formData.append('PreapprovalFormId', data.PreapprovalFormId);
                formData.append('File', data.file);
                formData.append('Description', data.description);
                return {
                    url: 'v1/preapproval/UploadDocument',
                    method: 'POST',
                    body: formData
                };
            },
            invalidatesTags: (result, error, { PreapprovalFormId }) => [{ type: 'PreApprovalDocument', id: PreapprovalFormId }]
        }),

        getPreApprovalDocumentsById: builder.query({
            query: (id) => ({
                url: `v1/preapproval/GetDocumentsById?Id=${id}`,
                method: 'GET'
            }),
            transformResponse: (response) => response?.data ?? [],
            providesTags: (result, error, id) => [{ type: 'PreApprovalDocument', id }]
        }),

        deletePreApprovalDocument: builder.mutation({
            query: (data) => ({
                url: 'v1/preapproval/DeleteDocument',
                method: 'DELETE',
                body: data
            }),
            invalidatesTags: (result, error, { PreapprovalFormId }) => [{ type: 'PreApprovalDocument', id: PreapprovalFormId }]
        }),

        getPreApprovalCountBySapDealerId: builder.query({
            query: ({ sapDealerId }) => {
                return {
                    url: `v1/preapproval/getpreapprovalcountbysapdealerid`,
                    method: 'GET',
                    params: {
                        SapDealerId: sapDealerId
                    }
                };
            },
            providesTags: (result, error, arg) => [{ type: 'int', id: arg.warrClaimId }]
        }),

        getPreApprovalDropdown: builder.query({
            query: ({ preAppNo = '', chassisNo = '' } = {}) => {
                const params = {};
                if (preAppNo) params.preAppNo = preAppNo;
                if (chassisNo) params.chassisNo = chassisNo;
                return {
                    url: 'v1/preapproval/pre-approval-dropdown',
                    method: 'GET',
                    params
                };
            },
            providesTags: ['PreApproval']
        }),
        getPreApprovalComments: builder.query({
            query: ({ preApprovalId }) => {
                return {
                    url: 'v1/preapproval/pre-approval-comments',
                    method: 'GET',
                    params: { PreApprovalId: preApprovalId }
                };
            },
            transformResponse: (response) => {
                // ResponseResult(ResponseInfo<T>) pattern'inde genelde data'nın içinde geliyor
                const data = response?.data ?? response;

                const itemsRaw = data?.items ?? data?.Items ?? [];
                const totalCount = data?.totalCount ?? data?.TotalCount ?? 0;
                const statusCount = data?.statusCount ?? data?.StatusCount ?? 0;
                // Backend PascalCase dönebilir -> FE camelCase'e normalize edelim
                const items = (itemsRaw || []).map((x) => ({
                    commentDate: x.commentDate ?? x.CommentDate ?? null,
                    commentedBy: x.commentedBy ?? x.CommentedBy ?? '',
                    typeId: x.typeId ?? x.TypeId ?? 0,
                    typeName: x.typeName ?? x.TypeName ?? '',
                    dealerCode: x.dealerCode ?? x.DealerCode ?? null,
                    dealerName: x.dealerName ?? x.DealerName ?? null,
                    comment: x.comment ?? x.Comment ?? ''
                }));

                return { items, totalCount, statusCount };
            },
            providesTags: (result, error, arg) => [{ type: 'PreApproval', id: `comments-${arg?.preApprovalId}` }]
        })
    })
});

export const {
    useGetAllPreApprovalsQuery,
    useGetPreApprovalByIdQuery,
    useCreatePreApprovalMutation,
    useUpdatePreApprovalMutation,
    useDeletePreApprovalMutation,
    useChangePreApprovalStatusMutation,
    useGetPreApprovalHistoryQuery,
    useUploadPreApprovalDocumentMutation,
    useGetPreApprovalDocumentsByIdQuery,
    useLazyGetPreApprovalDocumentsByIdQuery,
    useDeletePreApprovalDocumentMutation,
    useGetPreApprovalCountBySapDealerIdQuery,
    useGetPreApprovalDropdownQuery,
    useGetPreApprovalCommentsQuery
} = preApprovalApi;
